import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Container, Typography, Paper } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { logError } from "../../services/logging-service";

/**
 * Enterprise-grade Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to error reporting service
        logError({
            error,
            errorInfo,
            componentStack: errorInfo.componentStack,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href,
        });

        // Update state with error details
        this.setState({
            error,
            errorInfo,
        });

        // Call optional error callback
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });

        if (this.props.onReset) {
            this.props.onReset();
        }
    };

    // eslint-disable-next-line class-methods-use-this
    handleReload() {
        window.location.reload();
    }

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            return (
                <Container maxWidth="md">
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "100vh",
                            py: 4,
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 4,
                                textAlign: "center",
                                borderRadius: 2,
                                backgroundColor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <ErrorOutlineIcon
                                sx={{
                                    fontSize: 80,
                                    color: "error.main",
                                    mb: 2,
                                }}
                            />
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                                {this.props.title || "Oops! Something went wrong"}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" paragraph>
                                {this.props.message ||
                                    "We're sorry for the inconvenience. An unexpected error has occurred. Our team has been notified and is working to fix it."}
                            </Typography>

                            {process.env.NODE_ENV === "development" && this.state.error && (
                                <Box
                                    sx={{
                                        mt: 3,
                                        p: 2,
                                        textAlign: "left",
                                        backgroundColor: "grey.100",
                                        borderRadius: 1,
                                        maxHeight: 200,
                                        overflow: "auto",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="pre"
                                        sx={{
                                            fontFamily: "monospace",
                                            fontSize: "0.75rem",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {this.state.error.toString()}
                                        {this.state.errorInfo &&
                                            `\n\n${this.state.errorInfo.componentStack}`}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.handleReset}
                                    startIcon={<RefreshIcon />}
                                    size="large"
                                >
                                    Try Again
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={this.handleReload}
                                    size="large"
                                >
                                    Reload Page
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                </Container>
            );
        }

        return this.props.children;
    }
}

ErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
    fallback: PropTypes.node,
    title: PropTypes.string,
    message: PropTypes.string,
    onError: PropTypes.func,
    onReset: PropTypes.func,
};

export default ErrorBoundary;
